import { nestedSearch } from './nestedSearch';
import { getDeletionState } from './contentDeletionState';

// The content list is one `content` row per (organization, location, type, uuid), written by the
// bac-python discovery jobs. One delivered film therefore arrives as many unrelated rows: the OV
// package, its VF(s), the KDMs that unlock them, and a Mediablock copy of each once ingested. This
// module folds those rows back into what a person means by "the film":
//
//   film group  ->  families (an OV with its VFs nested)  ->  keys under the CPL they unlock
//
// Two facts about the source data shape everything here (see dcp_parse.py / key_parse.py):
//  - `packageType` and `OV_uuid` are only stamped on BAC_server CPL rows. Mediablock rows share the
//    `cpl_uuid` of their BAC_server sibling, so per-CPL facts are merged across locations first.
//  - `movieVersion` is resolved by `movieversions.uuid == cpl_uuid` and nothing else, so an OV
//    companion or a key for an undelivered version has none. The film is then borrowed from any
//    family member that does, or — like bac-python's own OV fallback — from the DCNC film token
//    (`TheOdyssey_FTR-6…` -> `theodyssey`).

export const LOCATIONS = ['BAC_server', 'mediablock'];

export const CONTENT_SEARCH_FIELDS = [
    'type',
    'uuid',
    'cpl_uuid',
    'contentTitleText',
    { path: 'movieVersion.movie', fields: ['title', 'synopsis'] },
];

const filmTokenOf = title => (title || '').split('_')[0].trim();

const firstTitle = rows => rows.find(r => r.contentTitleText)?.contentTitleText || '';

const newestOf = rows => rows.reduce((max, r) => Math.max(max, new Date(r.createdAt || 0).getTime()), 0);

// Only a populated movie counts — an un-populated ObjectId string has no title or poster to offer.
const movieOf = rows => rows.map(r => r.movieVersion?.movie).find(m => m && typeof m === 'object') || null;

// The cleanup job can leave two Mediablock rows for one CPL: the one marked for removal (and possibly
// abandoned) and a live duplicate its discovery re-inserted. The row that carries the removal request
// is the one with the timeline and the Restore action, so it represents the location; failures first,
// then whatever is newest.
const severity = row => {
    const deletion = getDeletionState(row);
    return deletion ? (deletion.needsAttention ? 2 : 1) : 0;
};
const representative = rows => [...rows].sort((a, b) =>
    severity(b) - severity(a) || new Date(b.createdAt || 0) - new Date(a.createdAt || 0))[0] || null;

const byLocationOf = rows => Object.fromEntries(LOCATIONS.map(l => [l, representative(rows.filter(r => r.location === l))]));

const locationsOf = rows => LOCATIONS.filter(l => rows.some(r => r.location === l));

// `packageType` from the BAC_server row is the truth (clairmeta reads the package itself). The title
// suffix is only a last resort for Mediablock-only rows that never went through the BAC server — a
// `…_VF` title can perfectly well be an OV package (Passenger 7.1).
const packageTypeOf = rows => {
    const stamped = rows.find(r => r.packageType);
    if (stamped) return stamped.packageType;
    const match = firstTitle(rows).match(/_(OV|VF)$/i);
    return match ? match[1].toUpperCase() : null;
};

// Mediablock lifecycle states a CPL can be in, as the status bar counts them: on its way there, marked
// for removal and waiting for the cleanup job, or a removal the job has given up on.
export const CONTENT_STATES = ['transferring', 'pending-deletion', 'removal-failed'];

// Every Mediablock row of the CPL counts: a failed request must surface even when a live duplicate
// sits next to it.
const statesOf = rows => {
    const bacRow = rows.find(r => r.location === 'BAC_server');
    const onMediablock = rows.some(r => r.location === 'mediablock');
    const deletions = rows.filter(r => r.location === 'mediablock').map(getDeletionState).filter(Boolean);
    return [
        bacRow?.ingestRequestedAt && !bacRow.ingestFailedAt && !onMediablock && 'transferring',
        deletions.some(d => !d.needsAttention) && 'pending-deletion',
        deletions.some(d => d.needsAttention) && 'removal-failed',
    ].filter(Boolean);
};

// How many packages sit in each Mediablock lifecycle state — the numbers the status bar shows, taken
// from the same identities the state filter matches so the chip and the filtered list always agree.
export const countStates = groups => {
    const counts = Object.fromEntries(CONTENT_STATES.map(state => [state, 0]));
    groups.forEach(group => group.families.forEach(family => family.packages.forEach(pkg =>
        pkg.states.forEach(state => { counts[state] += 1; }))));
    return counts;
};

const groupBy = (rows, keyOf) => {
    const map = new Map();
    rows.forEach(row => {
        const key = keyOf(row);
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(row);
    });
    return map;
};

// One entry per CPL, whatever the number of locations it sits on.
const buildIdentities = rows => {
    const cplRows = rows.filter(r => r.type === 'CPL' && r.cpl_uuid);
    return new Map([...groupBy(cplRows, r => r.cpl_uuid)].map(([cplUuid, group]) => [cplUuid, {
        kind: 'cpl',
        cpl_uuid: cplUuid,
        rows: group,
        byLocation: byLocationOf(group),
        locations: locationsOf(group),
        packageType: packageTypeOf(group),
        OV_uuid: group.find(r => r.OV_uuid)?.OV_uuid || null,
        movie: movieOf(group),
        contentTitleText: firstTitle(group),
        dcpSize: group.find(r => r.dcpSize)?.dcpSize || 0,
        // clairmeta's ContentKind (feature | trailer | transitional | test) — BAC_server rows only,
        // and not declared on the Node schema, so it may be absent.
        contentKind: (group.find(r => r.ContentKind)?.ContentKind || '').toLowerCase() || null,
        pinned: group.some(r => r.isPinned),
        states: statesOf(group),
        newestAt: newestOf(group),
        keys: [],
    }]));
};

// The BAC server keeps every KDM XML it was handed, so the same key (`uuid`) is regularly stored two
// or three times, plus once more once it reaches the Mediablock. One line per key, copies counted.
const buildKeys = rows => {
    const kdmRows = rows.filter(r => r.type === 'KDM');
    return [...groupBy(kdmRows, r => r.uuid || r._id)].map(([uuid, group]) => ({
        kind: 'key',
        uuid,
        rows: group,
        byLocation: byLocationOf(group),
        locations: locationsOf(group),
        cpl_uuid: group[0].cpl_uuid,
        contentTitleText: firstTitle(group),
        notValidBefore: group.find(r => r.KDM_notValidBefore)?.KDM_notValidBefore || null,
        notValidAfter: group.find(r => r.KDM_notValidAfter)?.KDM_notValidAfter || null,
        movie: movieOf(group),
        newestAt: newestOf(group),
    }));
};

// Follow OV_uuid links until we reach a CPL with no OV of its own (the real OV), or a uuid that is
// not on this server (the OV was never delivered — the family is still keyed by it).
const rootOf = (identity, identities) => {
    let current = identity;
    const seen = new Set();
    while (current.OV_uuid && !seen.has(current.cpl_uuid)) {
        seen.add(current.cpl_uuid);
        const next = identities.get(current.OV_uuid);
        if (!next) return current.OV_uuid;
        current = next;
    }
    return current.cpl_uuid;
};

const byNewest = (a, b) => b.newestAt - a.newestAt;

const buildFamilies = (identities, keys) => {
    const orphanKeys = [];
    keys.forEach(key => {
        const identity = identities.get(key.cpl_uuid);
        if (identity) identity.keys.push(key);
        else orphanKeys.push(key);
    });

    const byRoot = new Map();
    identities.forEach(identity => {
        const root = rootOf(identity, identities);
        if (!byRoot.has(root)) byRoot.set(root, { kind: 'family', root, ov: null, vfs: [] });
        const family = byRoot.get(root);
        if (identity.cpl_uuid === root) family.ov = identity;
        else family.vfs.push(identity);
    });

    const families = [...byRoot.values()].map(family => {
        family.vfs.sort(byNewest);
        const packages = [family.ov, ...family.vfs].filter(Boolean);
        const packageKeys = packages.flatMap(p => p.keys);
        const rows = [...packages, ...packageKeys].flatMap(m => m.rows);
        return {
            ...family,
            ovMissing: !family.ov,
            packages,
            rows,
            movie: movieOf(rows),
            tokens: new Set(packages.map(p => filmTokenOf(p.contentTitleText).toLowerCase()).filter(Boolean)),
            tokenLabel: filmTokenOf(packages[0].contentTitleText),
            locations: locationsOf(rows),
            newestAt: newestOf(rows),
            keyCount: packageKeys.length,
        };
    });

    return { families, orphanKeys };
};

// A film group is keyed by the movie when any member resolved one, otherwise by the DCNC film token.
// Token-keyed leftovers are then folded into a movie group when exactly one movie group carries that
// token, which is what places a key for an undelivered SCOPE version under its film.
const buildGroups = (families, orphanKeys) => {
    const groups = new Map();
    const ensure = (key, seed) => {
        if (!groups.has(key)) {
            groups.set(key, { key, movie: seed.movie, tokenLabel: seed.tokenLabel, tokens: new Set(), families: [], orphanKeys: [] });
        }
        return groups.get(key);
    };
    const keyFor = unit => unit.movie ? `movie:${unit.movie._id}` : `token:${unit.tokens.values().next().value}`;

    families.forEach(family => {
        const group = ensure(keyFor(family), family);
        group.families.push(family);
        family.tokens.forEach(t => group.tokens.add(t));
    });
    orphanKeys.forEach(key => {
        const token = filmTokenOf(key.contentTitleText);
        const unit = { movie: key.movie, tokens: new Set([token.toLowerCase()]), tokenLabel: token };
        const group = ensure(keyFor(unit), unit);
        group.orphanKeys.push(key);
        group.tokens.add(token.toLowerCase());
    });

    const movieGroups = [...groups.values()].filter(g => g.movie);
    [...groups.values()].filter(g => !g.movie).forEach(group => {
        const hosts = movieGroups.filter(m => [...group.tokens].some(t => m.tokens.has(t)));
        if (hosts.length !== 1) return;
        hosts[0].families.push(...group.families);
        hosts[0].orphanKeys.push(...group.orphanKeys);
        group.tokens.forEach(t => hosts[0].tokens.add(t));
        groups.delete(group.key);
    });

    return [...groups.values()].map(group => {
        group.families.sort(byNewest);
        group.orphanKeys.sort(byNewest);
        const rows = [...group.families.flatMap(f => f.rows), ...group.orphanKeys.flatMap(k => k.rows)];
        return {
            key: group.key,
            title: group.movie?.title || group.tokenLabel || firstTitle(rows),
            posterUrl: group.movie?.poster?.imageUrl || null,
            movie: group.movie,
            families: group.families,
            orphanKeys: group.orphanKeys,
            rows,
            locations: locationsOf(rows),
            newestAt: newestOf(rows),
            counts: {
                packages: group.families.reduce((n, f) => n + f.packages.length, 0),
                keys: group.families.reduce((n, f) => n + f.keyCount, 0) + group.orphanKeys.length,
            },
        };
    }).sort(byNewest);
};

export const buildContentGroups = (rows = []) => {
    const identities = buildIdentities(rows);
    const keys = buildKeys(rows);
    const { families, orphanKeys } = buildFamilies(identities, keys);
    return buildGroups(families, orphanKeys);
};

// Where a key's window sits relative to now: 'valid' | 'upcoming' | 'expired' | null (no dates).
export const keyStatusOf = (notValidBefore, notValidAfter) => {
    if (!notValidBefore || !notValidAfter) return null;
    const now = new Date();
    if (new Date(notValidAfter) < now) return 'expired';
    if (new Date(notValidBefore) > now) return 'upcoming';
    return 'valid';
};

// Admins judge the dates the API copied from the best KDM onto the CPL row; organization roles only
// ever receive the coarse `kdmStatus`.
export const identityKeyStatus = (identity, admin) => {
    const row = identity.rows[0];
    return admin ? keyStatusOf(row.KDM_notValidBefore, row.KDM_notValidAfter) : (row.kdmStatus || null);
};

export const EMPTY_FILTERS = { location: [], kind: [], key: [], state: [], pinned: false };

export const hasActiveFilters = filters =>
    filters.location.length > 0 || filters.kind.length > 0 || filters.key.length > 0 || filters.state.length > 0 || filters.pinned;

const matchesLocation = (locations, selected) => !selected.length || locations.some(l => selected.includes(l));

const filterKeys = (keys, filters) => keys.filter(key =>
    matchesLocation(key.locations, filters.location) &&
    (!filters.key.length || filters.key.includes(keyStatusOf(key.notValidBefore, key.notValidAfter))));

// A CPL stays when it matches every active facet; its keys are narrowed by the same location and
// key-state facets. On the key facet a CPL also stays when one of its keys matches, so "expired key"
// surfaces a CPL that still has a live key alongside a lapsed one.
const filterIdentity = (identity, filters, admin) => {
    if (!matchesLocation(identity.locations, filters.location)) return null;
    if (filters.kind.length && !filters.kind.includes(identity.contentKind)) return null;
    if (filters.state.length && !identity.states.some(state => filters.state.includes(state))) return null;
    if (filters.pinned && !identity.pinned) return null;
    const keys = filterKeys(identity.keys, filters);
    if (filters.key.length && !keys.length && !filters.key.includes(identityKeyStatus(identity, admin))) return null;
    return { ...identity, keys };
};

// `ovMissing` is kept from the unfiltered family: an OV hidden by a filter is not an OV that never
// arrived, so the VFs simply render at the top level without the warning.
const filterFamily = (family, filters, admin) => {
    const ov = family.ov ? filterIdentity(family.ov, filters, admin) : null;
    const vfs = family.vfs.map(vf => filterIdentity(vf, filters, admin)).filter(Boolean);
    const packages = [ov, ...vfs].filter(Boolean);
    if (!packages.length) return null;
    return { ...family, ov, vfs, packages, keyCount: packages.reduce((n, p) => n + p.keys.length, 0) };
};

// Only what matches stays: members that fail a facet disappear, families and films with nothing
// left disappear with them. Orphan keys have no kind or Mediablock state and cannot be pinned, so
// those facets hide them.
export const filterContentGroups = (groups, filters = EMPTY_FILTERS, admin = true) => {
    if (!hasActiveFilters(filters)) return groups;
    return groups.map(group => {
        const families = group.families.map(f => filterFamily(f, filters, admin)).filter(Boolean);
        const orphanKeys = filters.kind.length || filters.state.length || filters.pinned ? [] : filterKeys(group.orphanKeys, filters);
        if (!families.length && !orphanKeys.length) return null;
        return {
            ...group,
            families,
            orphanKeys,
            counts: {
                packages: families.reduce((n, f) => n + f.packages.length, 0),
                keys: families.reduce((n, f) => n + f.keyCount, 0) + orphanKeys.length,
            },
        };
    }).filter(Boolean);
};

export const groupMatchesQuery = (group, query) =>
    !query || nestedSearch(group.rows, CONTENT_SEARCH_FIELDS, query).length > 0;

// The row the modal should open for a member: the copy on the location being viewed, else any.
export const pickRowForLocation = (byLocation, location) =>
    byLocation[location] || LOCATIONS.map(l => byLocation[l]).find(Boolean) || null;
